// Iter 307 — "Open inquiries" counter in the Commissions header + inbox dialog.
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, MessageSquareWarning } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const KIND = { review: ["Review", "bg-amber-100 text-amber-800"], question: ["Question", "bg-sky-100 text-sky-800"] };
const STATUS = { open: ["Unanswered", "bg-rose-100 text-rose-700"], answered: ["Answered", "bg-emerald-100 text-emerald-700"] };

function age(iso) {
  const h = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 36e5));
  return h < 1 ? "just now" : h < 24 ? `${h}h ago` : `${Math.round(h / 24)}d ago`;
}

export function OpenInquiriesButton() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const load = () => api.get("/commissions/inquiries", { params: { status: "open" } }).then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  if (!data) return null;
  const n = data.open || 0;
  return (
    <>
      <Button variant={n ? "default" : "outline"} onClick={() => setOpen(true)} data-testid="commissions-open-inquiries-btn"
              className={`h-9 ${n ? "bg-amber-600 hover:bg-amber-700 text-white" : ""}`}
              title="Partner questions and review requests that are not resolved yet">
        <MessageSquareWarning className="h-4 w-4" /> Open inquiries
        <span className="ml-1 rounded-full bg-white/90 px-1.5 text-xs font-semibold text-slate-800" data-testid="commissions-open-inquiries-count">{n}</span>
      </Button>
      {open && (
        <Dialog open onOpenChange={(v) => !v && setOpen(false)}>
          <DialogContent className="max-w-2xl" data-testid="open-inquiries-dialog">
            <DialogHeader>
              <DialogTitle>Open inquiries</DialogTitle>
              <DialogDescription>{data.unanswered || 0} unanswered · {n} not resolved. Click one to open the thread on the partner's page.</DialogDescription>
            </DialogHeader>
            {!data.items ? <Loader2 className="h-4 w-4 animate-spin" /> : data.items.length === 0 ? (
              <div className="py-6 text-center text-sm text-slate-500" data-testid="open-inquiries-empty">Nothing waiting — every partner question is resolved.</div>
            ) : (
              <div className="max-h-[60vh] divide-y overflow-y-auto" data-testid="open-inquiries-list">
                {data.items.map((i) => {
                  const last = i.messages?.[i.messages.length - 1];
                  return (
                    <button key={i.inquiry_id} type="button" onClick={() => { setOpen(false); navigate(i.link); }}
                            className="flex w-full items-start gap-3 py-2.5 text-left hover:bg-slate-50" data-testid={`open-inquiry-${i.inquiry_id}`}>
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-1.5 text-sm">
                          <span className="font-medium text-slate-800">{i.partner_name}</span>
                          <span className="text-slate-400">·</span>
                          <span className="text-slate-600">{i.period_label}</span>
                          <span className={`rounded-full px-1.5 py-0.5 text-[10px] font-medium ${KIND[i.kind]?.[1]}`}>{KIND[i.kind]?.[0]}</span>
                          <span className={`rounded-full px-1.5 py-0.5 text-[10px] font-medium ${STATUS[i.status]?.[1]}`}>{STATUS[i.status]?.[0]}</span>
                        </div>
                        <div className="mt-0.5 truncate text-xs text-slate-500">{last?.name}: {last?.body}</div>
                      </div>
                      <div className="shrink-0 text-[11px] text-slate-400">{age(last?.at || i.created_at)}</div>
                    </button>
                  );
                })}
              </div>
            )}
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}
